/**
 * Live presence feed — same sources as Status: config/env WS URL, HTTP bootstrap, cached directory, WebSocket updates.
 */
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import type { AgentStatusRow } from '../components/AgentTile';
import { parseAgentDirectoryPayload } from './agentDirectory';

function mergeLiveAndCachedAgents(live: AgentStatusRow[], cached: AgentStatusRow[]): AgentStatusRow[] {
  const m = new Map<string, AgentStatusRow>();
  for (const a of cached) m.set(a.id, a);
  for (const a of live) m.set(a.id, { ...m.get(a.id), ...a } as AgentStatusRow);
  return Array.from(m.values()).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

function workerOriginFromWs(ws: string): string {
  if (!ws.startsWith('ws')) return '';
  try {
    const normalized = ws.startsWith('wss://')
      ? `https://${ws.slice('wss://'.length)}`
      : `http://${ws.slice('ws://'.length)}`;
    return new URL(normalized).origin;
  } catch {
    return '';
  }
}

export function usePresenceFeed() {
  const { getToken } = useAuth();
  const [presenceWsUrlFromConfig, setPresenceWsUrlFromConfig] = useState('');
  const [liveAgents, setLiveAgents] = useState<AgentStatusRow[]>([]);
  const [cachedAgents, setCachedAgents] = useState<AgentStatusRow[]>([]);
  const [connected, setConnected] = useState(false);

  const presenceWsUrl =
    presenceWsUrlFromConfig && presenceWsUrlFromConfig.startsWith('ws')
      ? presenceWsUrlFromConfig
      : typeof import.meta !== 'undefined'
        ? (import.meta.env?.VITE_PRESENCE_WS_URL as string | undefined)
        : undefined;

  const workerOrigin = useMemo(() => workerOriginFromWs(presenceWsUrl ?? ''), [presenceWsUrl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      try {
        const res = await fetch(`${base}/api/config?keys=presence_ws_url`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const url = typeof data.presence_ws_url === 'string' ? data.presence_ws_url.trim() : '';
          if (url.startsWith('ws') && !cancelled) setPresenceWsUrlFromConfig(url);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!workerOrigin) return;
      try {
        const res = await fetch(`${workerOrigin}/agents/catalog`);
        if (!res.ok) return;
        const data = (await res.json()) as { agents?: { agent_id: number; payload: string }[] };
        const list: AgentStatusRow[] = [];
        for (const r of data.agents ?? []) {
          const a = parseAgentDirectoryPayload(r.agent_id, r.payload);
          if (a) list.push(a);
        }
        if (!cancelled) setCachedAgents(list);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workerOrigin]);

  useEffect(() => {
    if (!workerOrigin) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${workerOrigin}/`);
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as { agents?: AgentStatusRow[] };
        if (Array.isArray(data.agents) && data.agents.length > 0 && !cancelled) {
          setLiveAgents(data.agents.map((a) => ({ ...a, statusDuration: a.statusDuration ?? 0, avatar_url: null })));
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workerOrigin]);

  useEffect(() => {
    if (!presenceWsUrl?.startsWith('ws')) return;
    let ws: WebSocket | null = null;
    let cancelled = false;
    const connect = () => {
      try {
        ws = new WebSocket(presenceWsUrl);
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data as string) as { type?: string; agents?: AgentStatusRow[] };
            if (data.type === 'snapshot' && Array.isArray(data.agents)) {
              setLiveAgents(data.agents.map((a) => ({ ...a, statusDuration: a.statusDuration ?? 0, avatar_url: null })));
            }
          } catch {
            /* ignore */
          }
        };
        ws.onopen = () => setConnected(true);
        ws.onclose = () => {
          setConnected(false);
          if (!cancelled) setTimeout(connect, 5000);
        };
      } catch {
        setConnected(false);
      }
    };
    connect();
    return () => {
      cancelled = true;
      if (ws) ws.close();
    };
  }, [presenceWsUrl]);

  const agents = useMemo(() => mergeLiveAndCachedAgents(liveAgents, cachedAgents), [liveAgents, cachedAgents]);

  return {
    agents,
    connected,
    presenceWsUrl: presenceWsUrl ?? '',
    workerOrigin,
    hasCached: cachedAgents.length > 0,
  };
}
